/**
 * Property Transformer Utility
 *
 * Converts simplified property type strings (e.g., 'str', 'list[str]', 'int')
 * to JSON Schema format required by SchemaForm component.
 *
 * Examples:
 * - 'str' → { type: 'string' }
 * - 'int' → { type: 'number' }
 * - 'bool' → { type: 'boolean' }
 * - 'list[str]' → { type: 'array', items: { type: 'string' } }
 * - 'ModelName' → { type: 'object', properties: {...} }
 */

interface SchemaProperty {
  type: 'string' | 'number' | 'boolean' | 'array' | 'object';
  items?: SchemaProperty | { type: 'object'; properties: Record<string, SchemaProperty> };
  properties?: Record<string, SchemaProperty>;
  required?: boolean;
  description?: string;
}

/**
 * Parse a simplified property type string into a JSON Schema property
 */
export function parsePropertyType(
  typeString: string,
  availableModels?: Array<{ class_name: string; properties: Record<string, any> }>
): SchemaProperty {
  // Handle null/undefined
  if (!typeString) {
    return { type: 'string' };
  }

  // Trim whitespace
  const trimmed = typeString.trim();

  // Handle primitive types
  if (trimmed === 'str' || trimmed === 'string') {
    return { type: 'string' };
  }
  if (trimmed === 'int' || trimmed === 'number' || trimmed === 'float') {
    return { type: 'number' };
  }
  if (trimmed === 'bool' || trimmed === 'boolean') {
    return { type: 'boolean' };
  }

  // Handle list types: list[T]
  const listMatch = trimmed.match(/^list\[(.+)\]$/);
  if (listMatch) {
    const elementType = listMatch[1].trim();
    return {
      type: 'array',
      items: parsePropertyType(elementType, availableModels)
    };
  }

  // Handle model references (assuming it's a custom model name)
  // If a model name is provided and we have the models list, resolve it
  if (availableModels) {
    const referencedModel = availableModels.find(m => m.class_name === trimmed);
    if (referencedModel) {
      // Convert the referenced model's properties recursively
      const transformedProperties: Record<string, SchemaProperty> = {};
      Object.entries(referencedModel.properties).forEach(([key, value]) => {
        transformedProperties[key] = parsePropertyType(value as string, availableModels);
      });
      return {
        type: 'object',
        properties: transformedProperties
      };
    }
  }

  // If we can't recognize the type, default to string with a description
  return {
    type: 'string',
    description: `Type: ${trimmed}`
  };
}

/**
 * Transform a model's properties object from simplified format to JSON Schema
 */
export function transformModelProperties(
  properties: Record<string, string | any>,
  availableModels?: Array<{ class_name: string; properties: Record<string, any> }>
): Record<string, SchemaProperty> {
  const schema: Record<string, SchemaProperty> = {};

  Object.entries(properties).forEach(([key, value]) => {
    if (typeof value === 'string') {
      // Simple string format: 'str', 'list[str]', etc.
      schema[key] = parsePropertyType(value, availableModels);
    } else if (typeof value === 'object' && value.type) {
      // Already in JSON Schema format
      schema[key] = value as SchemaProperty;
    } else {
      // Unknown format, default to string
      schema[key] = { type: 'string' };
    }
  });

  return schema;
}
